import { useTranslation } from 'react-i18next';
import { Modal, Skeleton } from '@noor/ui';
import { dayjs, type ModalRenderProps } from '@noor/lib';
import { Can } from '@/entities/access';
import { useOrderNotes } from '@/entities/order-note';

/**
 * Every note of an order, newest as the API returns them — cpanel
 * `LastNoteColumn`'s dialog: the note text, then its date
 * (`DD.MM.YYYY HH:mm`) and author.
 */
export function OrderNotesModal({ payload }: ModalRenderProps<'order-notes'>) {
  const { t } = useTranslation('orders');
  const { data: notes, isLoading } = useOrderNotes(payload.orderId);

  return (
    <Can permission="orders.notes.view">
      <Modal title={t('notes.title')}>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : notes && notes.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {notes.map((note) => (
              <li key={note.id} className="rounded-xl bg-muted px-3 pt-3 pb-2">
                <p className="text-base font-medium break-words whitespace-pre-line">
                  {note.text}
                </p>
                <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="tabular-nums">
                    {note.created_at
                      ? dayjs(note.created_at).format('DD.MM.YYYY HH:mm')
                      : '-'}
                  </span>
                  <span>{note.created_by_name}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t('notes.empty')}</p>
        )}
      </Modal>
    </Can>
  );
}
